import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  Lock, LogOut, Inbox, Search, Loader2, RefreshCw, Phone, Mail,
  MapPin, Clock, Trash2, CheckCircle2, AlertTriangle, ChevronDown,
  BarChart3, List, Building2,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DuckBadge } from "@/components/DuckMascot";
import { verifyAdmin, fetchLeads, updateLead, deleteLead } from "@/lib/api";
import AdminStats from "@/components/AdminStats";
import AdminPros from "@/components/AdminPros";

const STATUSES = ["new", "contacted", "sold", "dead"];
const STATUS_STYLES = {
  new: "bg-duckgreen/15 text-forest border-duckgreen/30",
  contacted: "bg-terracotta/15 text-terracotta border-terracotta/30",
  sold: "bg-teal/15 text-teal border-teal/30",
  dead: "bg-forest/10 text-forest-950/50 border-forest/15",
};
const TOKEN_STORAGE = "dtb_admin_key";

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

function Login({ onAuthed }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!token.trim()) return;
    setBusy(true);
    try {
      await verifyAdmin(token.trim());
      localStorage.setItem(TOKEN_STORAGE, token.trim());
      onAuthed(token.trim());
    } catch {
      toast.error("Invalid admin key.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-forest-950 grain flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><DuckBadge size={72} /></div>
        <div className="rounded-3xl bg-cream p-7 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-2 text-forest-950/60">
            <Lock className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.2em] font-semibold">Admin access</span>
          </div>
          <h1 className="mt-3 font-serif text-2xl font-bold text-forest-950">Lead dashboard</h1>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Admin key"
            autoFocus
            className="mt-5 w-full px-4 py-3.5 rounded-xl border border-forest/20 bg-white text-forest-950 text-base focus:outline-none focus:ring-2 focus:ring-duckgreen focus:border-transparent"
            data-testid="admin-key-input"
          />
          <button
            onClick={submit}
            disabled={busy}
            className="mt-3 w-full px-6 py-3.5 rounded-xl bg-forest-950 text-cream font-semibold hover:bg-forest transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {busy ? "Checking…" : "Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-cream border-forest/10 text-forest-950",
    green: "bg-duckgreen/10 border-duckgreen/25 text-forest",
    warn: "bg-terracotta/10 border-terracotta/25 text-terracotta",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3.5 ${tones[tone]}`}>
      <div className="text-2xl font-bold leading-none">{value}</div>
      <div className="mt-1.5 text-[11px] uppercase tracking-[0.15em] opacity-70 font-semibold">{label}</div>
    </div>
  );
}

function Receipts({ receipts }) {
  if (!receipts?.length) {
    return (
      <div className="flex items-center gap-2 text-xs text-forest-950/45">
        <AlertTriangle className="w-3.5 h-3.5" />
        No buyer configured for this pest — not delivered.
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {receipts.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          {r.status === "delivered" ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-duckgreen shrink-0" />
          ) : (
            <AlertTriangle className="w-3.5 h-3.5 text-terracotta shrink-0" />
          )}
          <span className="font-semibold text-forest-950">{r.buyer_name}</span>
          <span className="text-forest-950/50">via {r.channel}</span>
          <span className="text-forest-950/35">{r.sent_at ? timeAgo(r.sent_at) : ""}</span>
          {r.error ? <span className="text-terracotta/80 truncate">{r.error}</span> : null}
        </div>
      ))}
    </div>
  );
}

function LeadCard({ record, token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(record.notes || "");
  const [saving, setSaving] = useState(false);
  const d = record.lead || {};
  const name = [d.first_name, d.last_name].filter(Boolean).join(" ") || "No name";

  const save = async (changes) => {
    setSaving(true);
    try {
      await updateLead(token, record.key, changes);
      toast.success("Saved");
      onChanged();
    } catch {
      toast.error("Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete lead from ${name}? This can't be undone.`)) return;
    try {
      await deleteLead(token, record.key);
      toast.success("Deleted");
      onChanged();
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  return (
    <div className="rounded-2xl border border-forest/12 bg-cream overflow-hidden" data-testid="admin-lead-card">
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-serif text-lg font-bold text-forest-950 truncate">{name}</span>
              <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${STATUS_STYLES[record.status] || STATUS_STYLES.new}`}>
                {record.status}
              </span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-forest-950/65">
              <span className="font-semibold text-forest">{record.pest || "Unspecified"}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{record.zip_code || "—"}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{timeAgo(record.received_at)}</span>
            </div>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="shrink-0 p-2 -m-1 rounded-lg hover:bg-pill/60 transition-colors"
            aria-label={open ? "Collapse" : "Expand"}
            data-testid="admin-lead-toggle"
          >
            <ChevronDown className={`w-5 h-5 text-forest-950/50 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {d.phone && (
            <a href={`tel:${d.phone}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-forest-950 text-cream text-sm font-semibold active:scale-95 transition-transform">
              <Phone className="w-3.5 h-3.5" /> {d.phone}
            </a>
          )}
          {d.email && (
            <a href={`mailto:${d.email}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-forest/20 text-forest-950 text-sm font-semibold active:scale-95 transition-transform">
              <Mail className="w-3.5 h-3.5" /> Email
            </a>
          )}
        </div>

        <div className="mt-3"><Receipts receipts={record.receipts} /></div>
      </div>

      {open && (
        <div className="border-t border-forest/10 bg-sand/50 p-4 sm:p-5 space-y-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            {[
              ["Urgency", d.urgency], ["Property", d.property_type],
              ["Prefers", d.preferred_contact], ["Consent", d.consent],
              ["Landing page", d.page], ["Source", d.utm_source || "direct"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold">{k}</dt>
                <dd className="text-forest-950/80 break-words">{v || "—"}</dd>
              </div>
            ))}
          </dl>

          {d.description && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold">What they said</div>
              <p className="mt-1 text-sm text-forest-950/80 leading-relaxed">{d.description}</p>
            </div>
          )}

          <div>
            <div className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => save({ status: s })}
                  disabled={saving}
                  className={`px-3.5 py-2 rounded-full text-sm font-semibold border transition-all active:scale-95 ${
                    record.status === s ? STATUS_STYLES[s] : "bg-white border-forest/15 text-forest-950/60"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-wider text-forest-950/45 font-semibold mb-2">Notes</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Who you sent it to, what happened…"
              className="w-full px-3.5 py-3 rounded-xl border border-forest/15 bg-white text-sm text-forest-950 focus:outline-none focus:ring-2 focus:ring-duckgreen"
            />
            <div className="mt-2 flex items-center justify-between gap-3">
              <button
                onClick={() => save({ notes })}
                disabled={saving || notes === (record.notes || "")}
                className="px-5 py-2.5 rounded-full bg-forest-950 text-cream text-sm font-semibold disabled:opacity-40 active:scale-95 transition-transform"
              >
                Save notes
              </button>
              <button
                onClick={remove}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full text-sm font-semibold text-terracotta hover:bg-terracotta/10 transition-colors"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [pestFilter, setPestFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [view, setView] = useState("leads");
  const [prosRefresh, setProsRefresh] = useState(0);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await fetchLeads(t);
      setRecords(res.leads || []);
      setAuthed(true);
    } catch (err) {
      if (String(err.message) === "unauthorized") {
        localStorage.removeItem(TOKEN_STORAGE);
        setAuthed(false);
        setToken("");
      } else {
        toast.error("Couldn't load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
    else setLoading(false);
  }, [token, load]);

  const pests = useMemo(
    () => ["All", ...Array.from(new Set(records.map((r) => r.pest).filter(Boolean)))],
    [records]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return records.filter((r) => {
      if (pestFilter !== "All" && r.pest !== pestFilter) return false;
      if (statusFilter !== "All" && r.status !== statusFilter) return false;
      if (!q) return true;
      const d = r.lead || {};
      return [d.first_name, d.last_name, d.email, d.phone, d.zip_code, d.description]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [records, query, pestFilter, statusFilter]);

  const stats = useMemo(() => {
    const undelivered = records.filter((r) => !r.receipts?.some((x) => x.status === "delivered")).length;
    return {
      total: records.length,
      newCount: records.filter((r) => r.status === "new").length,
      sold: records.filter((r) => r.status === "sold").length,
      undelivered,
    };
  }, [records]);

  const signOut = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE);
    setToken("");
    setAuthed(false);
    setRecords([]);
  }, []);

  const refresh = () => (view === "pros" ? setProsRefresh((k) => k + 1) : load(token));

  if (!authed && !loading) return <Login onAuthed={setToken} />;

  return (
    <div className="min-h-screen bg-sand">
      <Seo title="Lead Dashboard" description="DuckTheBugs admin" path="/admin" noindex />
      <header className="bg-forest-950 text-cream grain sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <DuckBadge size={36} />
            <div className="min-w-0">
              <div className="font-serif text-lg font-bold leading-tight">{view === "pros" ? "Pros" : "Leads"}</div>
              <div className="text-[11px] text-cream/55">{view === "pros" ? "Lead buyer applications" : `${stats.total} total`}</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={refresh} className="p-2.5 rounded-full hover:bg-cream/10 transition-colors" aria-label="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={signOut} className="p-2.5 rounded-full hover:bg-cream/10 transition-colors" aria-label="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        <div className="flex gap-2">
          {[["leads", "Leads", List], ["pros", "Pros", Building2], ["stats", "Stats", BarChart3]].map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => setView(id)}
              className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all ${
                view === id ? "bg-forest-950 text-cream border-forest-950" : "bg-cream text-forest-950/65 border-forest/15"
              }`}
              data-testid={`admin-tab-${id}`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {view === "pros" ? (
          <AdminPros token={token} refreshKey={prosRefresh} onUnauthorized={signOut} />
        ) : (<>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <Stat label="Total" value={stats.total} />
          <Stat label="New" value={stats.newCount} tone="green" />
          <Stat label="Sold" value={stats.sold} />
          <Stat label="Undelivered" value={stats.undelivered} tone={stats.undelivered ? "warn" : "default"} />
        </div>

        {view === "stats" ? <AdminStats records={records} /> : (<>
        <div className="space-y-2.5">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-forest-950/35" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, ZIP…"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-forest/12 bg-cream text-base text-forest-950 focus:outline-none focus:ring-2 focus:ring-duckgreen"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {pests.map((p) => (
              <button
                key={p}
                onClick={() => setPestFilter(p)}
                className={`shrink-0 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all ${
                  pestFilter === p ? "bg-forest-950 text-cream border-forest-950" : "bg-cream text-forest-950/65 border-forest/15"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {["All", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`shrink-0 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all ${
                  statusFilter === s ? "bg-teal text-white border-teal" : "bg-cream text-forest-950/65 border-forest/15"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-forest/40" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <Inbox className="w-10 h-10 mx-auto text-forest/20" />
            <p className="mt-4 text-forest-950/55">
              {records.length === 0 ? "No leads yet." : "Nothing matches those filters."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => (
              <LeadCard key={r.key} record={r} token={token} onChanged={() => load(token)} />
            ))}
          </div>
        )}
        </>)}
        </>)}
      </main>
    </div>
  );
}
